import React, { useEffect, useMemo, useState } from 'react';
import { Cake, Plus, X, Gift, Share2, Snowflake, ChevronLeft, ChevronRight, Bell, Trash2, Edit3, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import AppHeader from './AppHeader';

const REL_OPTIONS = ['My Child', 'Friend', 'Classmate', 'Cousin', 'Sibling', 'Family'];

const monthName = (m) => new Date(2000, m, 1).toLocaleString('en-US', { month: 'long' });

const relTint = (r) => ({
  'My Child': 'bg-gold/25 text-navy border-gold',
  'Friend': 'bg-teal/15 text-teal border-teal',
  'Classmate': 'bg-purple-100 text-purple-800 border-purple-300',
  'Cousin': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Sibling': 'bg-rose-100 text-rose-800 border-rose-300',
  'Family': 'bg-amber-100 text-amber-800 border-amber-300',
}[r] || 'bg-cream text-navy border-cream');

const BirthdayModal = ({ open, initial, onClose, onSaved }) => {
  const [form, setForm] = useState(() => initial || { name: '', dob: '', relationship: 'My Child', notes: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial || { name: '', dob: '', relationship: 'My Child', notes: '' }); }, [open, initial]);
  if (!open) return null;
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.dob) { toast.error('Name and date required'); return; }
    setBusy(true);
    try {
      if (initial?.id) await api.put(`/birthdays/${initial.id}`, form);
      else await api.post('/birthdays', form);
      toast.success(initial?.id ? 'Updated 🎂' : 'Birthday added 🎂');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="birthday-modal">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md shadow-squishyLg overflow-hidden">
        <div className="bg-gradient-to-r from-gold via-teal to-navy p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2"><Cake className="w-5 h-5" /><span className="font-heading font-bold">{initial?.id ? 'Edit birthday' : 'Add a birthday'}</span></div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center" data-testid="bday-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-navy/70 text-xs font-heading font-bold uppercase">Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body"
              placeholder="e.g. Emma" data-testid="bday-name" />
          </label>
          <label className="block">
            <span className="text-navy/70 text-xs font-heading font-bold uppercase">Date of birth</span>
            <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })}
              className="mt-1 w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body"
              data-testid="bday-dob" />
          </label>
          <div>
            <span className="text-navy/70 text-xs font-heading font-bold uppercase">Relationship</span>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="bday-rel">
              {REL_OPTIONS.map((r) => (
                <button type="button" key={r} onClick={() => setForm({ ...form, relationship: r })}
                  className={`h-10 rounded-xl border-2 font-heading font-bold text-xs ${form.relationship === r ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-slate-200 hover:border-teal'}`}
                  data-testid={`bday-rel-${r.replace(/\s+/g, '-')}`}>{r}</button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-navy/70 text-xs font-heading font-bold uppercase">Notes (optional)</span>
            <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
              placeholder="Interests, wishlist ideas…" data-testid="bday-notes" />
          </label>
          <button type="submit" disabled={busy}
            className="w-full h-12 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60"
            data-testid="bday-save">
            {busy ? 'Saving…' : (initial?.id ? 'Save changes' : 'Add birthday')}
          </button>
        </div>
      </form>
    </div>
  );
};

const GiftModal = ({ open, birthday, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !birthday) return;
    api.get(`/birthdays/gifts?age=${birthday.age_turning || 8}`).then(({ data }) => setData(data));
  }, [open, birthday]);
  if (!open || !birthday) return null;
  return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="gift-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-squishyLg" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 text-white flex items-center justify-between shrink-0" style={{ background: 'linear-gradient(135deg,#0F4C4C,#c47a2a)' }}>
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            <div>
              <div className="font-heading font-bold">Gifts for {birthday.name}</div>
              <div className="text-white/80 text-[11px]">Turning {birthday.age_turning} · in {birthday.days_until} days</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center" data-testid="gift-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {!data && <div className="text-center text-navy/60 py-8">Loading gift ideas…</div>}
          {data?.christmas?.active && (
            <div className="rounded-2xl p-3 text-white flex items-center gap-3 shadow-lg" style={{ background: 'linear-gradient(135deg,#0F4C4C,#8b1f2f)' }} data-testid="gift-xmas-banner">
              <Snowflake className="w-6 h-6 text-white/80 animate-pulse" />
              <div className="text-sm">
                <div className="font-heading font-bold">🎄 Gift it by Christmas</div>
                <div className="text-white/85 text-xs">{data.christmas.days_to_xmas} days to Dec 25 — complimentary gift wrap on all OuiStuPaStu orders.</div>
              </div>
            </div>
          )}
          <section data-testid="gifts-primary">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-gold" />
              <h3 className="font-heading font-bold text-navy">OuiStuPaStu — perfect fits</h3>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {data?.primary?.map((g) => (
                <a key={g.id} href={g.cta_url} target="_blank" rel="noopener noreferrer"
                  className={`rounded-2xl bg-white border-2 p-3 flex items-center gap-3 hover:scale-[1.01] transition-transform ${g.featured ? 'border-gold shadow-gold' : 'border-teal/40'}`}
                  data-testid={`gift-primary-${g.id}`}>
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl shrink-0 bg-gradient-to-br from-gold/25 to-teal/25">{g.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-heading font-bold text-navy text-sm truncate">{g.name}</div>
                      {g.featured && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gold text-navy font-heading font-bold">TOP PICK</span>}
                    </div>
                    <div className="text-navy/70 text-xs leading-snug">{g.desc}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-heading font-bold text-navy text-sm">${g.price_usd.toFixed(2)}</span>
                      <span className="text-teal text-[11px] font-bold inline-flex items-center gap-0.5">Buy now <ExternalLink className="w-3 h-3" /></span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </section>
          {data?.secondary?.length > 0 && (
            <section data-testid="gifts-secondary">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-navy/60 font-heading font-bold text-[11px] uppercase tracking-wider">Great Additions</span>
                <span className="text-navy/40 text-[10px]">complementary partner items</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {data.secondary.map((g) => (
                  <div key={g.id} className="rounded-2xl bg-cream p-3 border-2 border-cream" data-testid={`gift-secondary-${g.id}`}>
                    <div className="text-2xl">{g.emoji}</div>
                    <div className="font-heading font-bold text-navy text-xs mt-1 leading-tight">{g.name}</div>
                    <div className="text-navy/60 text-[11px] mt-0.5 leading-tight line-clamp-2">{g.desc}</div>
                    <div className="text-navy font-heading font-bold text-sm mt-1">${g.price_usd.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

const CalendarGrid = ({ birthdays }) => {
  const [monthOffset, setMonthOffset] = useState(0);
  const today = new Date();
  const view = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const firstDay = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
  const bdaysByDay = useMemo(() => {
    const map = {};
    (birthdays || []).forEach((b) => {
      if (!b.next_birthday) return;
      const nxt = new Date(b.next_birthday);
      if (nxt.getFullYear() === view.getFullYear() && nxt.getMonth() === view.getMonth()) {
        (map[nxt.getDate()] = map[nxt.getDate()] || []).push(b);
      }
    });
    return map;
  }, [birthdays, view.getFullYear(), view.getMonth()]);

  return (
    <section className="bg-white rounded-3xl p-4 shadow-squishy border-2 border-cream" data-testid="calendar-grid">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonthOffset(monthOffset - 1)} className="w-9 h-9 rounded-full bg-cream flex items-center justify-center" data-testid="cal-prev"><ChevronLeft className="w-4 h-4 text-navy" /></button>
        <div className="font-heading font-bold text-navy">{monthName(view.getMonth())} {view.getFullYear()}</div>
        <button onClick={() => setMonthOffset(monthOffset + 1)} className="w-9 h-9 rounded-full bg-cream flex items-center justify-center" data-testid="cal-next"><ChevronRight className="w-4 h-4 text-navy" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {['S','M','T','W','T','F','S'].map((d, i) => <div key={i} className="text-navy/50 text-[10px] font-heading font-bold uppercase">{d}</div>)}
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const list = bdaysByDay[day] || [];
          const isToday = view.getFullYear() === today.getFullYear() && view.getMonth() === today.getMonth() && day === today.getDate();
          return (
            <div key={day} className={`aspect-square rounded-xl flex flex-col items-center justify-center ${isToday ? 'bg-navy text-white' : list.length ? 'bg-gold/20' : 'bg-cream'}`}
              data-testid={`cal-day-${day}`}>
              <div className="text-xs font-heading font-bold">{day}</div>
              {list.length > 0 && <div className="text-[8px] leading-none">🎂 {list.length > 1 ? `x${list.length}` : ''}</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default function Birthdays() {
  const [data, setData] = useState({ birthdays: [], christmas: { active: false, days_to_xmas: 0 } });
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(60);
  const [editTarget, setEditTarget] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [giftTarget, setGiftTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/birthdays');
      setData(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upcoming = (data.birthdays || []).filter((b) => (b.days_until ?? 9999) <= horizon);
  const remove = async (b) => {
    if (!window.confirm(`Remove ${b.name}'s birthday?`)) return;
    try { await api.delete(`/birthdays/${b.id}`); toast.success('Removed'); await load(); }
    catch { toast.error('Failed'); }
  };
  const share = async (b) => {
    try {
      const { data: r } = await api.get(`/birthdays/${b.id}/share`);
      const url = window.location.origin + `/wishlist/${r.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Wishlist link copied to clipboard');
    } catch { toast.error('Share failed'); }
  };

  return (
    <div className="min-h-screen bg-cream pb-24" data-testid="birthdays-page">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <header className="rounded-3xl p-5 text-white shadow-squishyLg" style={{ background: 'linear-gradient(135deg,#c47a2a,#0F4C4C)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Cake className="w-5 h-5 text-gold" />
            <span className="text-gold font-heading font-bold text-[11px] uppercase tracking-wider">Birthdays & Gifts</span>
          </div>
          <h1 className="font-heading text-2xl font-bold">Never miss a birthday again 🎂</h1>
          <p className="text-white/80 text-sm mt-1">Add the kids in your life. We remind you 30 · 14 · 7 · 3 days ahead — and hand-pick the perfect gift.</p>
        </header>

        {data.christmas?.active && (
          <section className="rounded-3xl p-5 shadow-squishyLg text-white flex items-center gap-4"
            style={{ background: 'linear-gradient(135deg,#0F4C4C,#8b1f2f)' }} data-testid="xmas-banner">
            <Snowflake className="w-10 h-10 text-white/90 shrink-0 animate-pulse" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-lg">🎄 Christmas mode is ON</div>
              <div className="text-white/85 text-sm">
                <b>{data.christmas.days_to_xmas}</b> days until Dec 25. Order OuiStuPaStu gifts today — <b>complimentary gift wrapping</b> on every order.
              </div>
            </div>
            <button onClick={() => setGiftTarget({ name: 'Christmas', age_turning: 8, days_until: data.christmas.days_to_xmas })}
              className="h-11 px-4 rounded-full bg-gold text-navy font-heading font-bold text-sm shadow-gold shrink-0"
              data-testid="xmas-shop">Top Christmas gifts</button>
          </section>
        )}

        <div className="flex items-center gap-2 flex-wrap" data-testid="horizon-toggle">
          {[30, 60, 90].map((h) => (
            <button key={h} onClick={() => setHorizon(h)}
              className={`h-10 px-4 rounded-full font-heading font-bold text-sm ${horizon === h ? 'bg-navy text-white' : 'bg-white text-navy border-2 border-cream hover:border-teal/60'}`}
              data-testid={`horizon-${h}`}>Next {h} days</button>
          ))}
          <button onClick={() => { setEditTarget(null); setShowModal(true); }}
            className="ml-auto h-10 px-4 rounded-full bg-gold text-navy font-heading font-bold text-sm shadow-gold inline-flex items-center gap-1.5"
            data-testid="add-birthday">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>

        <CalendarGrid birthdays={data.birthdays} />

        <section data-testid="birthday-list">
          <div className="text-navy/60 font-heading font-bold text-[11px] uppercase tracking-wider mb-2">
            Upcoming ({upcoming.length})
          </div>
          {loading ? (
            <div className="text-center py-8 text-navy/60">Loading…</div>
          ) : upcoming.length === 0 ? (
            <div className="bg-white rounded-3xl p-6 text-center border-2 border-cream" data-testid="birthday-empty">
              <div className="text-4xl mb-2">🎂</div>
              <p className="text-navy/70 text-sm">No birthdays in the next {horizon} days. Add one to start receiving proactive reminders.</p>
              <button onClick={() => { setEditTarget(null); setShowModal(true); }}
                className="mt-3 h-11 px-5 rounded-full bg-gold text-navy font-heading font-bold text-sm shadow-gold"
                data-testid="add-birthday-empty">Add a birthday</button>
            </div>
          ) : (
            <div className="space-y-2">
              {upcoming.map((b) => {
                const soon = b.days_until <= 7;
                return (
                  <div key={b.id} className={`bg-white rounded-2xl p-3 border-2 flex items-center gap-3 ${soon ? 'border-gold shadow-gold' : 'border-cream'}`} data-testid={`bday-row-${b.id}`}>
                    <div className="w-14 h-14 rounded-2xl flex flex-col items-center justify-center shrink-0 bg-gradient-to-br from-gold/20 to-teal/20">
                      <div className="text-2xl leading-none">🎂</div>
                      <div className="text-navy font-heading font-bold text-[10px] mt-0.5">in {b.days_until}d</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-heading font-bold text-navy truncate">{b.name}</div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-heading font-bold ${relTint(b.relationship)}`}>{b.relationship}</span>
                      </div>
                      <div className="text-navy/60 text-xs">Turning {b.age_turning} · {new Date(b.next_birthday).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                      {(b.active_reminders || []).length > 0 && (
                        <div className="text-gold text-[10px] font-bold inline-flex items-center gap-1 mt-0.5"><Bell className="w-3 h-3" /> Reminder tier: {Math.max(...b.active_reminders)}d</div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button onClick={() => setGiftTarget(b)}
                        className="h-9 px-3 rounded-full bg-gold text-navy font-heading font-bold text-xs shadow-gold inline-flex items-center gap-1"
                        data-testid={`find-gift-${b.id}`}><Gift className="w-3.5 h-3.5" /> Find a gift</button>
                      <div className="flex gap-1">
                        <button onClick={() => share(b)} className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-teal/15" data-testid={`share-${b.id}`} title="Share wishlist"><Share2 className="w-3.5 h-3.5 text-navy" /></button>
                        <button onClick={() => { setEditTarget(b); setShowModal(true); }} className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-teal/15" data-testid={`edit-${b.id}`} title="Edit"><Edit3 className="w-3.5 h-3.5 text-navy" /></button>
                        <button onClick={() => remove(b)} className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-red-50 hover:text-red-500" data-testid={`del-${b.id}`} title="Remove"><Trash2 className="w-3.5 h-3.5 text-navy" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <BirthdayModal open={showModal} initial={editTarget} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />
      <GiftModal open={!!giftTarget} birthday={giftTarget} onClose={() => setGiftTarget(null)} />
    </div>
  );
}
